import Head from "next/head";

// ** React
import { useEffect, useState } from "react";

// ** Components
import CategoryCard from "@/components/CategoryCard";
import Pagination from "@/components/Pagination";
import { Container, Grid2 as Grid, Typography } from "@mui/material";

// ** API
import articlesApi from "@/api/articles/api";
import technologiesApi from "@/api/technologies/api";

// ** Layout
import DefaultLayout from "../../../layout/DefaultLayout";

export async function getStaticPaths() {
  // Fetch all technologies to get their slugs
  const technologies = await technologiesApi.getTechnologies();

  // Map over the technologies to create paths
  const paths = technologies.data.map((technology) => ({
    params: { technology: technology.slug },
  }));

  return {
    paths,
    fallback: false, // See explanation below
  };
}

export async function getStaticProps({ params }) {
  const { technology } = params;
  const page = 1;
  const pageSize = 8;

  const articlesRes = await articlesApi.getPaginatedArticlesByTechnology(
    technology,
    page,
    pageSize
  );

  return {
    props: {
      technologyWithArticles: articlesRes,
      technology,
    },
    revalidate: 10, // Re-generate the page every 10 seconds
  };
}
function Technology({ technologyWithArticles, technology }) {
  // const { technology, articles } = technologyWithArticles;
  const [articlesData, setArticlesData] = useState(technologyWithArticles);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchArticles = async () => {
      const response = await articlesApi.getPaginatedArticlesByTechnology(
        technology,
        currentPage,
        8
      );
      setArticlesData(response);
    };
    fetchArticles();
  }, [currentPage, technology]);

  const siteUrl = "https://valentinmarko.com";
  const canonicalUrl = `${siteUrl}/blog/${technology}`;

  const title = `${technology} Articles | Valentin-Marko – Software Development Blog`;

  const description = `Explore ${technology} tutorials, articles, tips, and best practices. Learn ${technology} with practical examples and real-world use cases.`;

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta
          property="og:site_name"
          content="Valentin-Marko – Software Development Blog"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta
          property="og:image"
          content="https://valentinmarko.com/about.webp"
        />
      </Head>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: title,
            url: canonicalUrl,
            description,
            isPartOf: {
              "@type": "Blog",
              name: "Valentin Marko – Software Development Blog",
              url: `${siteUrl}/blog`,
            },
            about: { "@type": "Thing", name: technology },
            mainEntity: {
              "@type": "ItemList",
              itemListElement: articlesData.data.map((article, index) => ({
                "@type": "ListItem",
                position: index + 1,
                url: `${siteUrl}/blog/article/${article.slug}`,
              })),
            },
          }),
        }}
      />

      <Container sx={{ minHeight: "calc(100vh - 150px)" }}>
        <Grid container spacing={3} marginTop={"34px"} marginBottom={"34px"}>
          <Grid size={{ xs: 12 }}>
            <Typography
              component={"h1"}
              variant="h1"
              sx={{
                fontSize: { xs: "25px", sm: "35px", lg: "44px" },
                fontWeight: 900,
                color: "#2D3748",
                textTransform: "capitalize",
              }}
            >
              {technology}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography
              component={"p"}
              variant="h5"
              sx={{
                fontSize: { xs: "14px", sm: "18px", lg: "20px" },
                fontWeight: 600,
                color: "#718096",
                textTransform: "capitalize",
                marginBottom: "30px",
              }}
            >
              {`Explore the latest ${technology} articles, tips, and best practices.`}
            </Typography>
          </Grid>
          {articlesData.data.map((article) => (
            <Grid size={{ xs: 12, md: 6 }} key={article.id}>
              <CategoryCard
                slug={article.slug}
                topic={article.topic.name}
                topicSlug={article.topic.slug}
                title={article.name}
                description={article.shortDescription}
                articleImage={article.articleImageUrl}
                author={"Valentin-Marko Cîmpan"}
                date={article.updatedAt}
                readTime={"2min"}
              />
            </Grid>
          ))}
        </Grid>
        <Grid container>
          <Grid size={{ xs: 0, sm: 1, md: 2, lg: 3 }}></Grid>
          <Grid size={{ xs: 12, sm: 10, md: 8, lg: 6 }} marginTop={"auto"}>
            {articlesData.data && (
              <Pagination
                currentPage={currentPage}
                totalPages={articlesData.meta.pagination.pageCount}
                setCurrentPage={setCurrentPage}
              />
            )}
          </Grid>
          <Grid size={{ xs: 0, sm: 1, md: 2, lg: 3 }}></Grid>
        </Grid>
      </Container>
    </>
  );
}
Technology.getLayout = (page) => <DefaultLayout>{page}</DefaultLayout>;

export default Technology;
